<script lang="ts">
    import { getContext, onDestroy, onMount } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import { slide } from 'svelte/transition'

    import type { GenOrder } from '../../order'
    import Icon from '@iconify/svelte'
    import { orderBy } from 'lodash-es'
    import { toASCII, toUnicode } from 'punycode-esm'
    import { filter } from 'rxjs/operators'

    import { gettext as _, ngettext } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, spath } from 'hydria/svelte_helpers'
    import { numberToVisual } from 'hydria/utils/numbers'

    import { stream } from 'wdom/services/stream'
    import type { StreamEvent } from 'wdom/services/stream'

    import IntCurrency from 'wdom/components/IntCurrency.svelte'

    const Order = getContext('Order') as GenOrder
    const status = sderived(Order.status, ($status) => $status)
    const items = sderived(Order.items, ($items) => $items)
    const order = sderived(Order.orderStore, ($order) => $order)

    let inputElement: HTMLInputElement | undefined = $state()
    let value: string = $state('')
    let working = $state(false)

    // let domainUnicode = $state('')
    let domainUnicode = $derived(value ? toUnicode(value).toLocaleLowerCase() : '')
    let domainAscii = $derived(value ? toASCII(domainUnicode) : '')
    // let domainTld: string | undefined = $state(undefined)
    let domainTld: string | undefined = $derived(domainAscii.includes('.') ? domainAscii.split('.').at(-1) : '')
    // let domainName: string
    // let hasPremium = false

    let results: any[] = $state([])

    let sortedResults = $derived.by(() => {
        const prio = [...[domainTld ? domainTld : '-'], ...['ca', 'com', 'net', 'org']]
        return orderBy(
            results,
            [
                (p) => {
                    let n = prio.indexOf(p.tld)
                    if (n < 0) n = 99
                    return n
                },
                (n) => n.tld,
            ],
            ['asc', 'asc'],
        )
    })

    let domainsInOrder: Set<string> = $derived(new Set($status == 'ready' ? $items.filter((i) => i.type == 'DOMAIN').map((i) => i.meta.a_name) : []))

    let existingDomains = sderived(Order.existingDomains, ($v) => $v)

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    onMount(() => {
        if (inputElement) inputElement.focus()
    })

    let seqId = 0
    let requestTag: string
    let receivedCira = false
    let expectedTotalResponses: number = $state(0)
    let receivedTotalResponses: number = $state(0)

    let lookingUp: boolean = $derived(Boolean(expectedTotalResponses && receivedTotalResponses && receivedTotalResponses < expectedTotalResponses))

    unsubs.push(
        stream.pipe(filter((e) => (e as StreamEvent).event == 'lookup_progress')).subscribe({
            next: (e) => {
                let ev = e as StreamEvent
                // if (!lookingUp) {
                //     // Probably coming from another browser tab
                //     return
                // }
                if (ev.data.source == 'start') {
                    // Always the first message, and we don't care
                    return
                }

                if (ev.data['request_tag'] !== requestTag) {
                    console.warn('Ignoring unmatched lookup result', ev.data['request_tag'])
                    return
                }

                if (ev.data.source == 'cira') {
                    if (receivedCira) {
                        // Cira can return multiple answers in the case of a bundle.
                        // The first lookup will be, in that case, a direct check in our DB
                        expectedTotalResponses++
                    } else {
                        receivedCira = true
                    }
                }
                receivedTotalResponses++

                for (const [iname, item] of Object.entries(ev.data.domains) as any) {
                    const ascii = toASCII(iname)
                    // we actually need to split the ascii, and check for non-tld part.
                    // and.. with dashes and in other situations, OpenSRS returns
                    // 'similar' results.
                    // if (ascii !== domainAscii) {
                    //     console.warn('Received lookup event for an unexpected domain', ascii)
                    // }
                    const uni = toUnicode(iname)
                    const parts = uni.split('.')
                    const tld = parts.pop()
                    const name = parts.join('.')
                    const litem = {
                        unicode: uni,
                        ascii: ascii,
                        name: name,
                        tld: tld,
                        isAvail: item.available,
                        isPremium: false,
                        yearlyPrice: item.yearly_price,
                    }
                    // if (litem.isPremium) hasPremium = true
                    results.push(litem)
                }
            },
        }),
    )

    async function lookup() {
        let r: SimpleFetchResult

        working = true
        // hasPremium = false

        requestTag = `${++seqId}-${domainAscii}`
        expectedTotalResponses = 0
        receivedTotalResponses = 0
        receivedCira = false

        results = []

        try {
            r = await simpleFetch('POST', '/json/domain/1/lookup', { domain: domainAscii, owner: $order.owner, request_tag: requestTag, background: true })
            expectedTotalResponses = r.data.n_tasks
        } catch (e) {
            console.error('Lookup failure:', e)
            notify({
                type: 'danger',
                title: $_('Lookup'),
                text: 'La recherche de domaines a généré une erreur.',
            })
        }

        working = false
    }

    let adding: false | string = $state(false)
    async function addDomain(item: any) {
        adding = item.ascii
        await Order.addDomain(item.ascii, 1)
        adding = false
    }
</script>

<style>
    .ecom-dom-list {
        display: grid;
        grid-template-rows: auto;
        grid-template-columns: 15px 1fr 8em 3.5em 35px;
        align-items: center;
        grid-gap: 1em;
    }
    .edl-premium {
        font-size: 0.75em;
    }

    .edl-price,
    .edl-premium,
    .edl-action {
        justify-self: end;
    }

    .progress {
        --bs-progress-border-radius: 0.25rem;
    }
</style>

<div id="order-domain-lookup">
    <form
        onsubmit={(e) => {
            e.preventDefault()
            lookup()
        }}
        class="mb-2"
        style="display: flex; gap: 0.75em; align-items: baseline;"
    >
        <label for="order-add-domain">{$_('Recherche:')}</label>
        <input type="text" class="form-control" id="order-add-domain" style="flex: 1 0 0;" bind:this={inputElement} bind:value disabled={working} />
        <button type="submit" class="btn btn-primary" disabled={working || $status != 'ready'}><Icon inline icon="fa:search" /></button>
    </form>
    {#if $status == 'ready' && (lookingUp || working)}
        <div transition:slide|local={{ duration: 250 }}>
            <div class="progress mb-2">
                {#if $status != 'ready' || !(lookingUp || working)}
                    <div class="progress-bar">–</div>
                {:else if working}
                    <div class="progress-bar text-bg-info progress-bar-animated progress-bar-striped">{$_('Requesting')}</div>
                {:else}
                    <div class="progress-bar progress-bar-animated progress-bar-striped" style="min-width: 4em; width: {(receivedTotalResponses / expectedTotalResponses) * 100}%">
                        <!-- {receivedTotalResponses} / {expectedTotalResponses} -->
                        {numberToVisual((100 * receivedTotalResponses) / expectedTotalResponses, { units: '%', maxDecimals: 2 })}
                    </div>
                {/if}
            </div>
        </div>
    {/if}
    {#if results.length}
        <ul class="list-group" style="margin: 0">
            {#each sortedResults as item, i}
                <li class="list-group-item list-domain-admin">
                    <div class="ecom-dom-list">
                        <div class="edl-warning">
                            {#if $existingDomains[item.ascii]}
                                <a use:sanchor href={spath(`/ecom/orders?q=${item.unicode}`)} class="text-danger tt-right" data-tooltip={$ngettext('Existe dans {n} autre commande ouverte', 'Existe dans {n} autres commandes ouvertes', { n: $existingDomains[item.ascii] })}><Icon inline icon="fa:exclamation-triangle" /></a>
                            {/if}
                        </div>
                        <div class="edl-name">
                            <label for="dom-avail-{i}" class="for-button">{item.name}<strong>.{item.tld}</strong></label>
                        </div>
                        <div class="edl-price"><IntCurrency value={item.yearlyPrice} /></div>
                        <div class="edl-premium">
                            {#if item.isPremium}<span class="label label-danger">Premium</span>{/if}
                        </div>
                        <div class="edl-action">
                            <button id="dom-avail-{i}" type="button" class="btn domaillistinput btn-sm" disabled={!item.isAvail || domainsInOrder.has(item.ascii) || !!adding} class:btn-info={adding == item.ascii} class:btn-primary={adding !== item.ascii && item.isAvail && !domainsInOrder.has(item.ascii)} class:btn-success={adding !== item.ascii && domainsInOrder.has(item.ascii)} onclick={() => addDomain(item)}>
                                {#if adding == item.ascii}
                                    <div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                {:else}
                                    <Icon inline icon={item.isAvail && !domainsInOrder.has(item.ascii) ? 'fa:square-o' : !item.isAvail ? 'fa:minus-square-o' : 'fa:check-square-o'} />
                                {/if}
                            </button>
                        </div>
                    </div>
                </li>
            {/each}
        </ul>
    {/if}
</div>
